import { ReactNode } from 'react';
import { InfoTooltip } from './Tooltip';
import { LucideIcon } from 'lucide-react';

interface ModuleHeaderProps {
  icon?: React.ElementType;
  title: string;
  description: string;
  tooltip?: string;
  badge?: {
    text: string;
    variant?: 'primary' | 'success' | 'warning' | 'info';
  };
  actions?: ReactNode;
}

/**
 * ModuleHeader Component
 *
 * A consistent header for all module pages that provides clear visual hierarchy.
 */
export function ModuleHeader({
  icon: Icon,
  title,
  description,
  tooltip,
  badge,
  actions,
}: ModuleHeaderProps) {
  const badgeVariants = {
    primary: 'bg-primary-500/10 text-primary-400 border-primary-500/20',
    success: 'bg-success-500/10 text-success-400 border-success-500/20',
    warning: 'bg-warning-500/10 text-warning-400 border-warning-500/20',
    info: 'bg-info-500/10 text-info-400 border-info-500/20',
  };

  return (
    <div className="mb-8">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-4">
          {Icon && (
            <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-primary-500/10 border border-primary-500/20 shadow-subtle">
              <Icon className="w-6 h-6 text-primary-400" />
            </div>
          )}

          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-khiimori-text-primary">{title}</h1>
              {badge && (
                <span
                  className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${badgeVariants[badge.variant || 'primary']}`}
                >
                  {badge.text}
                </span>
              )}
              {tooltip && <InfoTooltip content={tooltip} />}
            </div>
          </div>
        </div>

        {actions && <div>{actions}</div>}
      </div>

      <p
        className={`text-khiimori-text-secondary text-base max-w-3xl leading-relaxed ${Icon ? 'ml-16' : ''}`}
      >
        {description}
      </p>

      <div
        className={`h-px mt-6 bg-gradient-primary opacity-30 ${Icon ? 'ml-16' : ''}`}
        style={{ transformOrigin: 'left' }}
      />
    </div>
  );
}

export function QuickTip({ children }: { children: ReactNode }) {
  return (
    <div className="mb-6 ml-16">
      <div className="inline-flex items-start gap-2 px-4 py-2.5 rounded-xl bg-info-500/10 border border-info-500/20 shadow-subtle">
        <svg
          className="w-4 h-4 text-info-500 mt-0.5 flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span className="text-sm text-khiimori-text-secondary">{children}</span>
      </div>
    </div>
  );
}
